import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Mail, User, MessageSquare, CheckCircle, Send, Instagram, Ticket } from 'lucide-react';
import { images } from '../data/mock';

const CONTACT_EMAIL = 'everything@8eight8.net';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent(`Aloha Tequila Fest Inquiry from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    
    // Open email client
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    
    // Show success message
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', message: '' });
    }, 3000);
  };

  return (
    <section id="contact" className="py-16 sm:py-24 bg-gradient-to-b from-gray-950 to-gray-900 relative overflow-hidden">
      {/* Decorative elements - hidden on mobile */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl hidden sm:block" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl hidden sm:block" />

      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black text-white text-center mb-3 sm:mb-4">
          Questions?
        </h2>
        <p className="text-white/60 text-center text-base sm:text-lg mb-4">
          We&apos;d love to hear from you!
        </p>
        
        {/* Direct Email Link */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <a 
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-pink-500/20 to-orange-500/20 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-pink-500/30 hover:border-pink-500/50 transition-all duration-300 hover:scale-105 group"
          >
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-pink-400 group-hover:animate-bounce" />
            <span className="text-white font-semibold text-sm sm:text-base">{CONTACT_EMAIL}</span>
          </a>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-5 sm:p-8 md:p-12 border border-white/10">
          {submitted ? (
            <div className="text-center py-12 animate-fadeIn">
              <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
              <h3 className="text-3xl font-bold text-white mb-4">Opening Email Client!</h3>
              <p className="text-white/80 text-lg">Your message is ready to send.</p>
              <p className="text-white/60 text-sm mt-2">If nothing opened, email us directly at:</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-pink-400 font-semibold hover:underline">
                {CONTACT_EMAIL}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 group-focus-within:text-pink-400 transition-colors" />
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-14 pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 focus:ring-pink-500/20 rounded-xl"
                    required
                  />
                </div>

                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 group-focus-within:text-pink-400 transition-colors" />
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-14 pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 focus:ring-pink-500/20 rounded-xl"
                    required
                  />
                </div>
              </div>

              <div className="relative group">
                <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-white/50 group-focus-within:text-pink-400 transition-colors" />
                <Textarea
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="min-h-[150px] pl-12 pt-4 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 focus:ring-pink-500/20 rounded-xl resize-none"
                  required
                />
              </div>

              <Button 
                type="submit"
                className="w-full h-14 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white text-lg font-bold rounded-xl shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3"
              >
                <Send className="w-5 h-5" />
                Send Message
              </Button>
              
              <p className="text-center text-white/50 text-sm">
                This will open your email app with your message ready to send
              </p>
            </form>
          )}
        </div>

        {/* QR Codes Section */}
        <div className="mt-10 sm:mt-14">
          <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-6">
            Scan & Connect
          </h3>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-10">
            {/* Instagram QR */}
            <a 
              href="https://www.instagram.com/alohatequilafest" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-lg hover:shadow-pink-500/30 transition-all duration-300 group-hover:scale-105">
                <img 
                  src={images.qrInstagram} 
                  alt="Follow us on Instagram" 
                  className="w-28 h-28 sm:w-36 sm:h-36"
                />
              </div>
              <div className="flex items-center justify-center gap-2 mt-3 text-white/70 group-hover:text-pink-400 transition-colors">
                <Instagram className="w-5 h-5" />
                <span className="font-semibold text-sm">Follow Us</span>
              </div>
            </a>

            {/* Tickets QR */}
            <a 
              href="https://alohacancer.ticketspice.com/aloha-tequila-arts-festival" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-lg hover:shadow-orange-500/30 transition-all duration-300 group-hover:scale-105">
                <img 
                  src={images.qrTickets} 
                  alt="Get Tickets" 
                  className="w-28 h-28 sm:w-36 sm:h-36"
                />
              </div>
              <div className="flex items-center justify-center gap-2 mt-3 text-white/70 group-hover:text-orange-400 transition-colors">
                <Ticket className="w-5 h-5" />
                <span className="font-semibold text-sm">Get Tickets</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
